import { system, reactive, effects } from 'kho';
import { Raycaster, Vector2 } from 'three';
import type { Camera } from 'three';
import { $camera, $renderer } from '../data/scene';
import { $editorMode, $instances, $instancesGroup, $selection } from '../data/model';

// DOM event the React drawer listens to, to highlight the selected committed
// object. Kept as a string literal so the editor layer stays free of component
// imports (mirrors IfcTabPanel's IFC_OBJECT_SELECTED_EVENT).
const IFC_OBJECT_SELECTED_EVENT = 'ifc-object-selected';

/**
 * Click-to-SELECT, as a kho system (replaces the imperative
 * DesignObjectDragHandler). Manipulation — move / rotate / tilt / height /
 * delete — is the gizmo's job (gizmoSystem); this system's only job is to write
 * the $selection atom on a genuine click:
 *   - click an instance  → select it (the gizmo attaches to $selection)
 *   - click empty space  → deselect
 *
 * It ignores gestures the gizmo consumed: the gizmo's capture-phase pointerdown
 * runs first and sets $editorMode='drag' when it grabs a handle, so a bubble-
 * phase pointerdown here can see that and bow out. A moved pointer (a drag) is
 * ignored too, so this never fights the gizmo.
 */
export const selectionSystem = system((scope) => {
  const { atoms } = scope(reactive);
  const { onDispose } = scope(effects);

  const raycaster = new Raycaster();
  raycaster.layers.enableAll();
  const ndc = new Vector2();
  let downX = 0;
  let downY = 0;
  let gizmoGrabbed = false;

  const canvas = () => atoms.get($renderer)?.domElement ?? null;

  const instanceIdOf = (obj: unknown): string | null => {
    let o = obj as { userData?: { instanceId?: string }; parent?: unknown } | null;
    while (o) {
      const id = o.userData?.instanceId;
      if (id) return id;
      o = o.parent as typeof o;
    }
    return null;
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    downX = e.clientX;
    downY = e.clientY;
    // The gizmo's capture-phase pointerdown already ran; if it grabbed a handle
    // it set editorMode='drag'. Don't treat this gesture as a selection click.
    gizmoGrabbed = atoms.get($editorMode) === 'drag';
  };

  const onUp = (e: PointerEvent) => {
    if (e.button !== 0 || gizmoGrabbed) return;
    // a drag (pointer moved) is not a selection click
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > 4) return;

    const el = canvas();
    const camera = atoms.get($camera) as Camera | null;
    const grp = atoms.get($instancesGroup);
    if (!el || !camera || !grp) return;

    const rect = el.getBoundingClientRect();
    if (
      e.clientX < rect.left || e.clientX > rect.right ||
      e.clientY < rect.top || e.clientY > rect.bottom
    ) {
      return;
    }
    ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1),
    );
    raycaster.setFromCamera(ndc, camera);
    // recursive: a factory object may be a Group whose child meshes carry the
    // instanceId (instancesSystem tags every descendant).
    const hit = raycaster.intersectObjects(grp.children, true)[0]?.object ?? null;
    const id = hit ? instanceIdOf(hit) : null;

    atoms.set($selection, id);

    // Notify the React drawer (highlight) with the persisted committedId, if any.
    const inst = id ? atoms.get($instances).get(id) : null;
    const committedId = inst?.committedId ?? null;
    if (typeof document !== 'undefined') {
      document.dispatchEvent(
        new CustomEvent(IFC_OBJECT_SELECTED_EVENT, { detail: { id: committedId } }),
      );
    }
  };

  // window listeners in the BUBBLE phase → they run after the gizmo's capture-
  // phase pointerdown, so gizmoGrabbed reflects a handle grab on the same event.
  window.addEventListener('pointerdown', onDown);
  window.addEventListener('pointerup', onUp);
  onDispose(() => {
    window.removeEventListener('pointerdown', onDown);
    window.removeEventListener('pointerup', onUp);
  });
});
